// RecordChatter — Polymorphic activity/comment timeline.
// Kullanım:
//   <RecordChatter recordType="prospect_account" recordId={selected.id} />
// Backend: /api/record-activities (GET list, POST, PUT, PATCH /complete, DELETE).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { toast } from './Toast';
import Skeleton from './Skeleton';

const KINDS = [
  { key: 'comment', label: 'Yorum',    icon: '💬', color: 'var(--text-secondary)' },
  { key: 'call',    label: 'Arama',    icon: '📞', color: 'var(--info)' },
  { key: 'meeting', label: 'Toplantı', icon: '👥', color: 'var(--accent-primary)' },
  { key: 'email',   label: 'E-posta',  icon: '✉️', color: 'var(--warning)' },
  { key: 'note',    label: 'Not',      icon: '📝', color: 'var(--text-tertiary)' },
  { key: 'task',    label: 'Görev',    icon: '✓',  color: 'var(--success)' },
];

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'az önce';
  if (diff < 3600) return Math.floor(diff / 60) + ' dk önce';
  if (diff < 86400) return Math.floor(diff / 3600) + ' saat önce';
  if (diff < 7 * 86400) return Math.floor(diff / 86400) + ' gün önce';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDue(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function userColor(userId) {
  if (!userId) return '#94a3b8';
  // Deterministic hash → 12 renk paletinden seç
  const palette = ['#ef4444', '#f59e0b', '#84cc16', '#10b981', '#06b6d4', '#3b82f6',
                   '#6366f1', '#a855f7', '#ec4899', '#f43f5e', '#14b8a6', '#0ea5e9'];
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

export default function RecordChatter({ recordType, recordId, currentUserId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ kind: 'comment', body: '', title: '', dueAt: '' });
  const [showSchedule, setShowSchedule] = useState(false);
  const [posting, setPosting] = useState(false);

  const fetchTimeline = useCallback(async () => {
    if (!recordType || !recordId) return;
    setLoading(true);
    try {
      const r = await api.get('/record-activities', { params: { type: recordType, id: recordId } });
      if (r.data.success) setItems(r.data.data || []);
    } catch (e) {
      console.error('chatter fetch:', e);
    } finally { setLoading(false); }
  }, [recordType, recordId]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  const handlePost = async () => {
    if (!draft.body.trim() && !draft.title.trim()) {
      toast.error('Mesaj boş olamaz');
      return;
    }
    setPosting(true);
    try {
      await api.post('/record-activities', {
        record_type: recordType,
        record_id: recordId,
        kind: draft.kind,
        title: draft.title || null,
        body: draft.body || null,
        due_at: draft.dueAt || null,
      });
      setDraft({ kind: 'comment', body: '', title: '', dueAt: '' });
      setShowSchedule(false);
      await fetchTimeline();
      toast.success('Eklendi');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Eklenemedi');
    } finally { setPosting(false); }
  };

  const handleComplete = async (id) => {
    try {
      await api.patch('/record-activities/' + id + '/complete');
      await fetchTimeline();
      toast.success('Tamamlandı');
    } catch (e) { toast.error('Tamamlanamadı'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Silinsin mi?')) return;
    try {
      await api.delete('/record-activities/' + id);
      await fetchTimeline();
      toast.success('Silindi');
    } catch (e) { toast.error(e.response?.data?.message || 'Silinemedi'); }
  };

  // Üstte: bekleyen görev/aktivite sayısı
  const pending = useMemo(() => items.filter(i => i.due_at && !i.completed_at).length, [items]);

  if (!recordType || !recordId) {
    return (
      <div style={{ padding: 20, color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
        Aktivite akışı görmek için önce kayıt seçin.
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      {/* Composer */}
      <div style={S.composer}>
        <div style={S.kindRow}>
          {KINDS.map(k => (
            <button
              key={k.key}
              type="button"
              onClick={() => setDraft(d => ({ ...d, kind: k.key }))}
              style={{
                ...S.kindBtn,
                ...(draft.kind === k.key ? S.kindBtnActive : null),
                color: draft.kind === k.key ? k.color : 'var(--text-secondary)',
                borderColor: draft.kind === k.key ? k.color : 'var(--border-input)',
              }}
              title={k.label}
            >
              <span style={{ fontSize: 14 }}>{k.icon}</span>
              <span>{k.label}</span>
            </button>
          ))}
        </div>

        {(draft.kind === 'meeting' || draft.kind === 'call' || draft.kind === 'task') && (
          <input
            type="text"
            placeholder="Başlık (opsiyonel)"
            value={draft.title}
            onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
            style={S.titleInput}
          />
        )}

        <textarea
          rows={2}
          placeholder={
            draft.kind === 'comment' ? 'Yorum yaz... (Ctrl+Enter ile gönder)' :
            draft.kind === 'call'    ? 'Görüşme özeti...' :
            draft.kind === 'meeting' ? 'Toplantı notu...' :
            draft.kind === 'email'   ? 'E-posta özeti...' :
            draft.kind === 'task'    ? 'Görev açıklaması...' :
            'Not...'
          }
          value={draft.body}
          onChange={e => setDraft(d => ({ ...d, body: e.target.value }))}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handlePost();
            }
          }}
          style={S.textarea}
        />

        {showSchedule && (
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
              Tarih ve saat
            </label>
            <input
              type="datetime-local"
              value={draft.dueAt}
              onChange={e => setDraft(d => ({ ...d, dueAt: e.target.value }))}
              style={S.dateInput}
            />
          </div>
        )}

        <div style={S.composerFooter}>
          <button
            type="button"
            onClick={() => setShowSchedule(s => !s)}
            style={{ ...S.linkBtn, color: showSchedule ? 'var(--accent-primary)' : 'var(--text-secondary)' }}
          >
            {showSchedule ? '✕ Planlama kapat' : '🕐 Sonraya planla'}
          </button>
          <button
            type="button"
            onClick={handlePost}
            disabled={posting}
            style={S.postBtn}
          >
            {posting ? '...' : 'Ekle'}
          </button>
        </div>
      </div>

      {/* Header */}
      <div style={S.header}>
        <span>Aktivite akışı</span>
        {pending > 0 && (
          <span style={S.badge}>{pending} bekleyen</span>
        )}
      </div>

      {/* Timeline */}
      {loading && items.length === 0 && (
        <div style={{ padding: 12 }}>
          <Skeleton width="80%" height={14} style={{ marginBottom: 8 }} />
          <Skeleton width="60%" height={14} style={{ marginBottom: 16 }} />
          <Skeleton width="70%" height={14} style={{ marginBottom: 8 }} />
          <Skeleton width="50%" height={14} />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div style={S.empty}>Henüz aktivite yok. İlk yorumu/aramayı/notu sen ekle.</div>
      )}

      <div style={S.timeline}>
        {items.map(it => {
          const kind = KINDS.find(k => k.key === it.kind) || KINDS[0];
          const isMine = currentUserId && it.user_id === currentUserId;
          const isOverdue = it.due_at && !it.completed_at && new Date(it.due_at) < new Date();
          return (
            <div key={it.id} style={{ ...S.item, ...(isOverdue ? S.itemOverdue : null) }}>
              <div style={{ ...S.avatar, background: userColor(it.user_id) }}>
                {getInitials(it.user_name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.itemHead}>
                  <span style={S.itemUser}>{it.user_name || 'Sistem'}</span>
                  <span style={{ ...S.kindBadge, color: kind.color, borderColor: kind.color }}>
                    {kind.icon} {kind.label}
                  </span>
                  <span style={S.itemTime}>{fmtTime(it.created_at)}</span>
                  {isMine && (
                    <button type="button" onClick={() => handleDelete(it.id)} style={S.iconBtn} title="Sil">🗑</button>
                  )}
                </div>
                {it.title && <div style={S.itemTitle}>{it.title}</div>}
                {it.body && <div style={S.itemBody}>{it.body}</div>}
                {it.due_at && (
                  <div style={S.dueRow}>
                    <span style={{ ...S.dueBadge, ...(isOverdue ? S.dueOverdue : null), ...(it.completed_at ? S.dueDone : null) }}>
                      {it.completed_at ? '✓ Tamamlandı' : (isOverdue ? '⚠ Gecikti: ' : '🕐 ') + fmtDue(it.due_at)}
                    </span>
                    {!it.completed_at && (
                      <button type="button" onClick={() => handleComplete(it.id)} style={S.completeBtn}>
                        Tamamla
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', height: '100%' },
  composer: {
    padding: 12,
    background: 'var(--bg-card-hover)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    marginBottom: 12,
  },
  kindRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 },
  kindBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 9px',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    border: '1px solid',
    borderRadius: 'var(--r-full)',
    background: 'var(--bg-card)',
    cursor: 'pointer',
    transition: 'all 0.12s',
  },
  kindBtnActive: {
    background: 'var(--bg-secondary)',
    boxShadow: 'var(--shadow-xs)',
  },
  titleInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid var(--border-input)',
    borderRadius: 'var(--r-sm)',
    fontSize: 'var(--text-sm)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    marginBottom: 6,
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid var(--border-input)',
    borderRadius: 'var(--r-sm)',
    fontSize: 'var(--text-sm)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    resize: 'vertical',
    fontFamily: 'inherit',
    marginBottom: 6,
  },
  dateInput: {
    padding: '6px 10px',
    border: '1px solid var(--border-input)',
    borderRadius: 'var(--r-sm)',
    fontSize: 'var(--text-sm)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
  },
  composerFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'var(--text-xs)',
    fontWeight: 500,
    padding: 0,
  },
  postBtn: {
    padding: '6px 16px',
    background: 'var(--accent-primary)',
    color: 'var(--text-on-accent)',
    border: 'none',
    borderRadius: 'var(--r-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 'var(--text-sm)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: '0 4px 8px',
  },
  badge: {
    background: 'var(--warning-bg)',
    color: 'var(--warning)',
    padding: '2px 8px',
    borderRadius: 'var(--r-full)',
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'none',
  },
  empty: {
    padding: 20,
    textAlign: 'center',
    fontSize: 'var(--text-sm)',
    color: 'var(--text-tertiary)',
    fontStyle: 'italic',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    overflow: 'auto',
    flex: 1,
  },
  item: {
    display: 'flex',
    gap: 10,
    padding: 10,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
  },
  itemOverdue: {
    borderColor: 'var(--warning)',
    background: 'var(--warning-bg)',
  },
  avatar: {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: '50%',
    color: 'white',
    fontSize: 11,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  itemUser: {
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  kindBadge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    padding: '1px 6px',
    border: '1px solid',
    borderRadius: 'var(--r-full)',
  },
  itemTime: {
    fontSize: 'var(--text-xs)',
    color: 'var(--text-tertiary)',
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-tertiary)',
    padding: 2,
    marginLeft: 'auto',
    fontSize: 12,
  },
  itemTitle: {
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: 2,
  },
  itemBody: {
    fontSize: 'var(--text-sm)',
    color: 'var(--text-secondary)',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.45,
  },
  dueRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 },
  dueBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 'var(--r-sm)',
    background: 'var(--info-bg)',
    color: 'var(--info)',
  },
  dueOverdue: { background: 'var(--danger-bg)', color: 'var(--danger)' },
  dueDone: { background: 'var(--success-bg)', color: 'var(--success)', textDecoration: 'line-through', opacity: 0.7 },
  completeBtn: {
    padding: '2px 8px',
    fontSize: '0.7rem',
    fontWeight: 600,
    background: 'var(--success-bg)',
    color: 'var(--success)',
    border: '1px solid var(--success)',
    borderRadius: 'var(--r-sm)',
    cursor: 'pointer',
  },
};
